/**
 * The storefront's API client, generated over the B20 contracts: one call
 * path for every §11 operation the browser may use. Fail-closed in both
 * directions — the request is validated against the operation's schema
 * before it touches the wire, the response after. Writes carry the client
 * idempotency key (Inv 4); callers retrying an action MUST pass the same
 * key back (the flow reducer keeps it for them).
 */

import { API_TIMEOUT, DeadlineExceededError, fetchWithDeadline, observer } from "@transact/observe";
import { operations, tolerantResponse, wireRequestId } from "@transact/contracts";

/** Pinned to packages/authorizer/src/handler.ts — the authorizer's tenant wall. */
export const TENANT_HEADER = "x-transact-tenant";

/** The kernel's §11 failure envelope, typed for the UI. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  /** B551 — the reference id (failure body, else `apigw-requestid`); shown verbatim. */
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, retryable: boolean, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    if (requestId !== undefined) this.requestId = requestId;
  }
}

/**
 * B169(b) — a refusal raised BEFORE the wire. The request did not match
 * the operation's schema, so nothing was sent: not a wire failure, not a
 * contended resource, not anything the person reading the screen can fix
 * by retrying. It is a builder's error, and it gets its own type so the
 * UI can give it its own face instead of the buyer's generic "try again".
 * The raw zod dump stays inside `fields` — it never becomes a message.
 */
export class ApiRequestError extends Error {
  readonly operation: string;
  /** The refusing field paths, dotted, for the builder reading the console. */
  readonly fields: readonly string[];

  constructor(operation: string, fields: readonly string[]) {
    super(
      `the page asked for an operation in a shape the service does not accept${
        fields.length > 0 ? ` (fields: ${fields.join(", ")})` : ""
      }`,
    );
    this.name = "ApiRequestError";
    this.operation = operation;
    this.fields = fields;
  }
}

/** Dotted field paths out of a zod-shaped refusal, without importing zod. */
function refusedFields(error: unknown): readonly string[] {
  const issues = (error as { issues?: unknown } | null)?.issues;
  if (!Array.isArray(issues)) return [];
  const paths = new Set<string>();
  for (const issue of issues) {
    const path = (issue as { path?: unknown }).path;
    if (Array.isArray(path) && path.length > 0) paths.add(path.map(String).join("."));
  }
  return [...paths];
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly tenantId: string;
  /** Session token supplier — absent or undefined means anonymous. */
  readonly token?: () => string | undefined;
  readonly fetchImpl?: typeof fetch;
  /** Client-key mint for writes; defaults to crypto.randomUUID. */
  readonly newKey?: () => string;
  /** B552 (c2) — the wire's deadline (default 12 s). */
  readonly deadlineMs?: number;
}

export interface CallOptions {
  /** Reuse a key to make a retry idempotent (Inv 4). */
  readonly clientKey?: string;
}

export interface ApiClient {
  call(name: string, fields: Record<string, unknown>, options?: CallOptions): Promise<unknown>;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  /* B552 (c2) — no request may hang forever: every call rides the deadline. */
  const fetchImpl = fetchWithDeadline(options.fetchImpl ?? ((input, init) => fetch(input, init)), {
    ...(options.deadlineMs !== undefined ? { deadlineMs: options.deadlineMs } : {}),
  });
  observer.wire({ baseUrl: options.baseUrl, tenantId: () => options.tenantId });
  const newKey = options.newKey ?? (() => crypto.randomUUID());

  return {
    async call(name, fields, callOptions) {
      observer.crumb(`op ${name}`);
      const op = operations[name];
      if (op === undefined) throw new Error(`unknown operation: ${name}`); // vocab-ok: thrown developer guard, never rendered — refusal faces translate what reaches a screen
      if (op.kind === "internal") throw new Error(`internal operation is not callable from the storefront: ${name}`); // vocab-ok: thrown developer guard, never rendered — refusal faces translate what reaches a screen

      // B413 — a tenantless contract (device.announce's class) carries NO
      // tenant field at all; injecting one would fail its strict schema.
      const body: Record<string, unknown> =
        op.tenantless === true ? { ...fields } : { tenantId: options.tenantId, ...fields };
      if (op.kind === "write") body["clientKey"] = callOptions?.clientKey ?? newKey();
      /* B169(b): fail closed before the wire — and say WHICH kind of
         failure this is. An escaping ZodError used to reach the buyer as
         the generic retry banner; that is how the B169 boot hid. */
      let request: unknown;
      try {
        request = op.request.parse(body);
      } catch (error) {
        throw new ApiRequestError(name, refusedFields(error));
      }

      const headers: Record<string, string> = {
        "content-type": "application/json",
        [TENANT_HEADER]: options.tenantId,
      };
      const token = options.token?.();
      if (token !== undefined) headers["authorization"] = `Bearer ${token}`;

      let response: Response;
      try {
        response = await fetchImpl(`${options.baseUrl}/ops/${name}`, {
          method: "POST",
          headers,
          body: JSON.stringify(request),
        });
      } catch (error) {
        observer.report({ kind: "network", message: `${name}: ${error instanceof Error ? error.message : String(error)}` });
        /* B552 (c2) — a dead or slow network is a retryable refusal in
           words (translateRefusal → the retry sentence), never a raw
           TypeError reaching the page as "something went wrong". */
        if (error instanceof DeadlineExceededError) {
          throw new ApiError(0, API_TIMEOUT, "The booking service took too long to answer. Please try again.", true);
        }
        throw new ApiError(0, "API_UNREACHABLE", "We couldn’t reach the booking service. Check your connection and try again.", true);
      }

      if (!response.ok) throw await toApiError(response);
      // B168: parse with the additive-tolerant twin — a cached buyer bundle
      // must survive additive server growth. A refusal HERE is a real shape
      // break, surfaced in words, never a raw zod dump (TRA-202).
      try {
        return tolerantResponse(op.response).parse(await response.json());
      } catch {
        observer.report({ kind: "contract", message: `${name} answered in a shape this build does not recognize` });
        throw new ApiError(
          0,
          "API_ANSWER_SHAPE",
          `The service answered ${name} in a shape this app does not recognize — the page you have open is probably out of date. Refresh the page; if it keeps happening, the site needs an update.`,
          false,
        );
      }
    },
  };
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "INTERNAL";
  let message = `request failed with status ${response.status}`;
  let retryable = response.status >= 500;
  let requestId = wireRequestId(undefined, response.headers?.get?.("apigw-requestid"));
  try {
    const parsed: unknown = JSON.parse(await response.text());
    requestId = wireRequestId(parsed, response.headers?.get?.("apigw-requestid"));
    if (parsed !== null && typeof parsed === "object") {
      const envelope = parsed as { code?: unknown; message?: unknown; retryable?: unknown };
      if (typeof envelope.code === "string") code = envelope.code;
      if (typeof envelope.message === "string" && envelope.message.length > 0) message = envelope.message;
      if (typeof envelope.retryable === "boolean") retryable = envelope.retryable;
    }
  } catch {
    /* non-JSON body: keep the defaults */
  }
  return new ApiError(response.status, code, message, retryable, requestId);
}
